<template>
  <CoreInput
      v-maska="phoneMask"
      name="phone_number"
      :label="label"
      label-position="above"
      :placeholder="phonePlaceHolder"
      v-model="phoneNumber.value.value"
      :has-error="error !== null"
      :hint-text="error"
      @input="handleInput($event)"
      @focusin="handleFocus($event)"
      :color-mode="colorMode"
      :is-disabled="disabled"
  >
  </CoreInput>
</template>

<script setup>
import CoreInput from '@/components/core/InputGroup.vue';
import * as yup from "yup";
import {phone} from "@/yup/validations";
import {getCountryCodeForRegionCode, parsePhoneNumber} from "awesome-phonenumber";
import {useField, useForm} from "vee-validate";
import {computed, onMounted, watch} from "vue";

yup.addMethod(yup.StringSchema, 'phone', phone);

const props = defineProps({
    regionCode: {
        type: String,
        default: 'US'
    },
    value: {
        type: String,
        required: true
    },
    valid: {
        type: Boolean,
        required: false
    },
    error: {
        type: String,
        default: null
    },
    label: {
        type: String,
        default: null
    },
    colorMode: {
        type: String,
        default: 'light',
    },
    initialValue: {
        type: String,
        default: "",
    },
    shouldEmitFocus: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    }
});

const emit = defineEmits([
    'update:value',
    'update:valid',
    'focusin'
])

const initialValues = computed(() => {
    return {
        phone_number:
            props.initialValue !== null ? props.initialValue : "",
    };
});

const regionCode = computed(() => {
    if (props.initialValue !== null) {
        let phoneNumber =  parsePhoneNumber(props.initialValue);

        return phoneNumber.regionCode ?? props.regionCode;
    } else {
        return props.regionCode;
    }
});

const {meta, setFieldValue} = useForm({
    initialValues: initialValues
});

const countryCode = getCountryCodeForRegionCode(regionCode.value);
const phoneNumber = useField('phone_number', yup.string().phone().label('The phone number'));
const phoneMask = '+' + countryCode + ' (###) ###-####';
const phonePlaceHolder = '+' + countryCode + ' (___) ___-____';

const error = computed(() => {
    if (props.error !== null) {
        return props.error;
    }

    if (phoneNumber.errorMessage.value !== undefined) {
        return phoneNumber.errorMessage.value;
    }

    return null;
});

watch(phoneNumber.errorMessage, function (value) {
    if (value !== undefined) {
        emit('update:valid', false);
    } else {
        emit('update:valid', true);
    }
});

function handleFocus(event) {
    if (props.shouldEmitFocus) {
        emit('focusin', event);
    }
}

function handleInput(event) {
    emit('update:value', event.target.value)
    emit('update:valid', phoneNumber.meta.valid);
}

onMounted(() => {
    setFieldValue('phone_number', props.initialValue);
    emit('update:valid', phoneNumber.meta.valid);
});

watch(() => props.value, (value) => {
    setFieldValue('phone_number', value);
    emit('update:valid', phoneNumber.meta.valid);
});
</script>

